import React from 'react';
import { Button, Input, InputNumber, Checkbox, Table, Tag, Tabs, Popconfirm, Space } from 'antd';
import {
  VerticalAlignTopOutlined, LeftOutlined, RightOutlined, VerticalAlignBottomOutlined,
  PlusOutlined, EditOutlined, CopyOutlined, DeleteOutlined, SaveOutlined, RollbackOutlined,
} from '@ant-design/icons';
import useMaterialForm from './hooks/useMaterialForm';

const { TextArea } = Input;

const formatDate = (v: string | null | undefined) => {
  if (!v) return '';
  try { return new Date(v).toLocaleDateString('zh-TW'); } catch { return v; }
};

const P1111Form: React.FC = () => {
  const f = useMaterialForm();
  const modeLabel = { browse: '瀏覽', add: '新增', edit: '修改', copy: '複製' }[f.mode];
  const modeColor = { browse: 'blue', add: 'green', edit: 'orange', copy: 'purple' }[f.mode];

  const fabricColumns = [
    { title: '公司代號', dataIndex: 'f1111_02', width: 150,
      render: (v: string, _: any, idx: number) => f.isEditing
        ? <Input value={v} onChange={e => f.updateFabric(idx, 'f1111_02', e.target.value)} maxLength={12} />
        : v },
    { title: '公司布號', dataIndex: 'f1111_11',
      render: (v: string, _: any, idx: number) => f.isEditing
        ? <Input value={v} onChange={e => f.updateFabric(idx, 'f1111_11', e.target.value)} maxLength={15} />
        : v },
    ...(f.isEditing ? [{ title: '操作', width: 60,
      render: (_: any, __: any, idx: number) => <Button type="link" danger size="small" onClick={() => f.removeFabric(idx)}>刪除</Button>
    }] : []),
  ];

  const grid = { display: 'grid', gridTemplateColumns: '120px 1fr 120px 1fr', gap: '10px 16px', alignItems: 'center' } as const;

  return (
    <div style={{ padding: 16, maxWidth: 1000 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>原料建檔</h3>
        <Tag color={modeColor}>{modeLabel}</Tag>
        {f.records.length > 0 && <span style={{ color: '#999', fontSize: 12 }}>{f.currentIdx + 1}/{f.records.length}</span>}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Input.Search placeholder="原料代號" allowClear disabled={f.isEditing} style={{ width: 160 }} onSearch={f.quickSearch} enterButton />
        <Button icon={<VerticalAlignTopOutlined />} disabled={f.isEditing || f.currentIdx <= 0} onClick={f.navFirst} />
        <Button icon={<LeftOutlined />} disabled={f.isEditing || f.currentIdx <= 0} onClick={f.navPrev} />
        <Button icon={<RightOutlined />} disabled={f.isEditing || f.currentIdx >= f.records.length - 1} onClick={f.navNext} />
        <Button icon={<VerticalAlignBottomOutlined />} disabled={f.isEditing || f.currentIdx >= f.records.length - 1} onClick={f.navLast} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {!f.isEditing ? (<>
          <Button type="primary" icon={<PlusOutlined />} onClick={f.startAdd}>新增</Button>
          <Button icon={<EditOutlined />} disabled={!f.current} onClick={f.startEdit}>修改</Button>
          <Button icon={<CopyOutlined />} disabled={!f.current} onClick={f.startCopy}>複製</Button>
          <Popconfirm title="確認刪除？" onConfirm={f.doDelete} okText="確認" cancelText="取消">
            <Button danger icon={<DeleteOutlined />} disabled={!f.current}>刪除</Button>
          </Popconfirm>
        </>) : (<>
          <Button type="primary" icon={<SaveOutlined />} onClick={f.save}>存檔</Button>
          <Button icon={<RollbackOutlined />} onClick={f.cancel}>還原</Button>
        </>)}
      </div>

      <Tabs defaultActiveKey="1" items={[
        { key: '1', label: '基本資料', children: (
          <div style={grid}>
            <span>原料代號：</span>
            <Input value={f.code} onChange={e => f.setCode(e.target.value.toUpperCase())} disabled={f.mode === 'edit' || f.mode === 'browse'} maxLength={6} style={{ width: 120 }} />
            <span>舊代號：</span>
            <Checkbox checked={f.active} onChange={e => f.setActive(e.target.checked)} disabled={!f.isEditing} />
            <span>原料名稱：</span>
            <div style={{ gridColumn: 'span 3' }}>
              <TextArea value={f.name} onChange={e => f.setName(e.target.value)} disabled={!f.isEditing} rows={2} />
            </div>
            <span>說明：</span>
            <div style={{ gridColumn: 'span 3' }}>
              <TextArea value={f.description} onChange={e => f.setDescription(e.target.value)} disabled={!f.isEditing} rows={2} />
            </div>
            <span>原料規格：</span>
            <div style={{ gridColumn: 'span 3' }}>
              <TextArea value={f.spec} onChange={e => f.setSpec(e.target.value)} disabled={!f.isEditing} rows={2} />
            </div>
            <span style={{ fontWeight: 600 }}>幅寬</span>
            <div style={{ gridColumn: 'span 3', display: 'flex', gap: 16, alignItems: 'center' }}>
              <span>針孔內</span>
              <InputNumber value={f.innerWidth} onChange={v => f.setInnerWidth(v || 0)} disabled={!f.isEditing} style={{ width: 100 }} />
              <span style={{ color: '#888' }}>INCH</span>
              <span style={{ marginLeft: 8 }}>針孔外</span>
              <InputNumber value={f.outerWidth} onChange={v => f.setOuterWidth(v || 0)} disabled={!f.isEditing} style={{ width: 100 }} />
              <span style={{ color: '#888' }}>INCH</span>
            </div>
            <span style={{ fontWeight: 600 }}>重量</span>
            <div style={{ gridColumn: 'span 3', display: 'flex', gap: 16, alignItems: 'center' }}>
              <span>碼重</span>
              <InputNumber value={f.yardWeight} onChange={v => f.setYardWeight(v || 0)} disabled={!f.isEditing} style={{ width: 100 }} />
              <span style={{ color: '#888' }}>G/YD</span>
              <span style={{ marginLeft: 8 }}>米平方重</span>
              <InputNumber value={f.gsm} onChange={v => f.setGsm(v || 0)} disabled={!f.isEditing} style={{ width: 100 }} />
              <span style={{ color: '#888' }}>G/SQM</span>
            </div>
            <span>研發樣品代號：</span>
            <Input value={f.sampleCode} onChange={e => f.setSampleCode(e.target.value)} disabled={!f.isEditing} maxLength={6} style={{ width: 120 }} />
            <span>工廠布號：</span>
            <Input value={f.factoryNo} onChange={e => f.setFactoryNo(e.target.value)} disabled={!f.isEditing} maxLength={15} style={{ width: 180 }} />
            <span>建檔人員：</span>
            <span>{f.current?.f111_27 || ''}</span>
            <span>建檔日期：</span>
            <span>{formatDate(f.current?.f111_29 as string)}</span>
            <span>最近異動日：</span>
            <span>{formatDate(f.current?.f111_28 as string)}</span>
            <span />
            <span />
          </div>
        )},
        { key: '2', label: `公司布號對照 (${f.fabrics.length})`, children: (
          <div>
            {f.isEditing && <Space style={{ marginBottom: 8 }}>
              <Button icon={<PlusOutlined />} onClick={f.addFabric}>新增</Button>
            </Space>}
            <Table dataSource={f.fabrics} columns={fabricColumns} rowKey={(_, idx) => String(idx)}
              size="small" pagination={false} scroll={{ y: 250 }} />
          </div>
        )},
      ]} />
    </div>
  );
};

export default P1111Form;
